import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";


/*
|--------------------------------------------------------------------------
| PLATFORM API
|--------------------------------------------------------------------------
|
| IMPORTANT:
|
| Platform dashboard must use platformAccessToken.
| Never use normal tenant accessToken here.
|--------------------------------------------------------------------------
*/

const platformApi =
  new API({
    tokenKey:
      "platformAccessToken",
  });


/*
|--------------------------------------------------------------------------
| RESPONSE HELPER
|--------------------------------------------------------------------------
*/

const getResponseData = (
  response
) => {

  return (
    response?.data ??
    response ??
    null
  );

};


/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorPayload = (
  error,
  fallbackMessage
) => {

  return {
    message:
      error?.message ||
      fallbackMessage,

    errorCode:
      error?.errorCode ||
      error?.code ||
      null,

    statusCode:
      error?.statusCode ||
      error?.status ||
      null,

    details:
      error?.details ||
      null,
  };

};


/*
|--------------------------------------------------------------------------
| BUILD QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
  params = {}
) => {

  const searchParams =
    new URLSearchParams();

  Object.entries(
    params || {}
  ).forEach(
    ([
      key,
      value,
    ]) => {

      if (
        value ===
          undefined ||
        value ===
          null ||
        value ===
          ""
      ) {
        return;
      }

      searchParams.append(
        key,
        String(
          value
        )
      );

    }
  );

  const queryString =
    searchParams.toString();

  return queryString
    ? `?${queryString}`
    : "";

};


/*
|--------------------------------------------------------------------------
| GET COMPLETE PLATFORM DASHBOARD
|--------------------------------------------------------------------------
|
| GET /v1/platform/dashboard
|--------------------------------------------------------------------------
*/

export const fetchPlatformDashboard =
  createAsyncThunk(
    "platformDashboard/fetchDashboard",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            `${
              config.getPlatformDashboard
            }${
              buildQueryString(
                params
              )
            }`
          );

        return getResponseData(
          response
        );

      } catch (error) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch platform dashboard."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| GET DASHBOARD SUMMARY
|--------------------------------------------------------------------------
|
| GET /v1/platform/dashboard/summary
|--------------------------------------------------------------------------
*/

export const fetchPlatformDashboardSummary =
  createAsyncThunk(
    "platformDashboard/fetchSummary",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            `${
              config.getPlatformDashboardSummary
            }${
              buildQueryString(
                params
              )
            }`
          );

        return getResponseData(
          response
        );

      } catch (error) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch platform dashboard summary."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| GET RECENT BUSINESSES
|--------------------------------------------------------------------------
|
| GET /v1/platform/dashboard/recent-businesses
|--------------------------------------------------------------------------
*/

export const fetchPlatformRecentBusinesses =
  createAsyncThunk(
    "platformDashboard/fetchRecentBusinesses",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            `${
              config.getPlatformRecentBusinesses
            }${
              buildQueryString(
                params
              )
            }`
          );

        return getResponseData(
          response
        );

      } catch (error) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch recent businesses."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| GET BUSINESS GROWTH
|--------------------------------------------------------------------------
|
| GET /v1/platform/dashboard/business-growth
|--------------------------------------------------------------------------
*/

export const fetchPlatformBusinessGrowth =
  createAsyncThunk(
    "platformDashboard/fetchBusinessGrowth",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            `${
              config.getPlatformBusinessGrowth
            }${
              buildQueryString(
                params
              )
            }`
          );

        return getResponseData(
          response
        );

      } catch (error) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch platform business growth."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| GET SUBSCRIPTION SUMMARY
|--------------------------------------------------------------------------
|
| GET /v1/platform/dashboard/subscriptions
|--------------------------------------------------------------------------
*/

export const fetchPlatformSubscriptionSummary =
  createAsyncThunk(
    "platformDashboard/fetchSubscriptionSummary",

    async (
      params = {},
      {
        rejectWithValue,
      }
    ) => {

      try {

        const response =
          await platformApi.get(
            `${
              config.getPlatformSubscriptionSummary
            }${
              buildQueryString(
                params
              )
            }`
          );

        return getResponseData(
          response
        );

      } catch (error) {

        return rejectWithValue(
          getErrorPayload(
            error,
            "Unable to fetch platform subscription summary."
          )
        );

      }

    }
  );


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

  /*
  |--------------------------------------------------------------------------
  | COMPLETE DASHBOARD
  |--------------------------------------------------------------------------
  */

  dashboard:
    null,

  dashboardLoading:
    false,

  dashboardError:
    null,


  /*
  |--------------------------------------------------------------------------
  | SUMMARY
  |--------------------------------------------------------------------------
  */

  summary:
    null,

  summaryLoading:
    false,

  summaryError:
    null,


  /*
  |--------------------------------------------------------------------------
  | RECENT BUSINESSES
  |--------------------------------------------------------------------------
  */

  recentBusinesses:
    [],

  recentBusinessesLoading:
    false,

  recentBusinessesError:
    null,


  /*
  |--------------------------------------------------------------------------
  | BUSINESS GROWTH
  |--------------------------------------------------------------------------
  */

  businessGrowth:
    [],

  businessGrowthLoading:
    false,

  businessGrowthError:
    null,


  /*
  |--------------------------------------------------------------------------
  | SUBSCRIPTIONS
  |--------------------------------------------------------------------------
  */

  subscriptions:
    null,

  subscriptionsLoading:
    false,

  subscriptionsError:
    null,


  /*
  |--------------------------------------------------------------------------
  | GENERAL
  |--------------------------------------------------------------------------
  */

  lastFetchedAt:
    null,
};


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformDashboardSlice =
  createSlice({

    name:
      "platformDashboard",

    initialState,

    reducers: {

      /*
      |--------------------------------------------------------------------------
      | RESET
      |--------------------------------------------------------------------------
      */

      resetPlatformDashboard: () =>
        initialState,


      /*
      |--------------------------------------------------------------------------
      | CLEAR ERRORS
      |--------------------------------------------------------------------------
      */

      clearPlatformDashboardErrors: (
        state
      ) => {

        state.dashboardError =
          null;

        state.summaryError =
          null;

        state.recentBusinessesError =
          null;

        state.businessGrowthError =
          null;

        state.subscriptionsError =
          null;

      },

    },


    /*
    |--------------------------------------------------------------------------
    | EXTRA REDUCERS
    |--------------------------------------------------------------------------
    */

    extraReducers: (
      builder
    ) => {

      /*
      |--------------------------------------------------------------------------
      | COMPLETE DASHBOARD
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformDashboard.pending,
          (
            state
          ) => {

            state.dashboardLoading =
              true;

            state.dashboardError =
              null;

          }
        )

        .addCase(
          fetchPlatformDashboard.fulfilled,
          (
            state,
            action
          ) => {

            state.dashboardLoading =
              false;

            state.dashboardError =
              null;

            state.dashboard =
              action.payload ||
              null;

            state.lastFetchedAt =
              new Date()
                .toISOString();

          }
        )

        .addCase(
          fetchPlatformDashboard.rejected,
          (
            state,
            action
          ) => {

            state.dashboardLoading =
              false;

            state.dashboardError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Unable to fetch platform dashboard.",
              };

          }
        );


      /*
      |--------------------------------------------------------------------------
      | SUMMARY
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformDashboardSummary.pending,
          (
            state
          ) => {

            state.summaryLoading =
              true;

            state.summaryError =
              null;

          }
        )

        .addCase(
          fetchPlatformDashboardSummary.fulfilled,
          (
            state,
            action
          ) => {

            state.summaryLoading =
              false;

            state.summaryError =
              null;

            state.summary =
              action.payload ||
              null;

          }
        )

        .addCase(
          fetchPlatformDashboardSummary.rejected,
          (
            state,
            action
          ) => {

            state.summaryLoading =
              false;

            state.summaryError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Unable to fetch platform dashboard summary.",
              };

          }
        );


      /*
      |--------------------------------------------------------------------------
      | RECENT BUSINESSES
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformRecentBusinesses.pending,
          (
            state
          ) => {

            state.recentBusinessesLoading =
              true;

            state.recentBusinessesError =
              null;

          }
        )

        .addCase(
          fetchPlatformRecentBusinesses.fulfilled,
          (
            state,
            action
          ) => {

            state.recentBusinessesLoading =
              false;

            state.recentBusinessesError =
              null;

            /*
            |--------------------------------------------------------------------------
            | Support either:
            |
            | data: [...]
            |
            | or
            |
            | data: {
            |   records: [...]
            | }
            |--------------------------------------------------------------------------
            */

            state.recentBusinesses =
              Array.isArray(
                action.payload
              )
                ? action.payload
                : action.payload
                    ?.records ||
                  action.payload
                    ?.businesses ||
                  [];

          }
        )

        .addCase(
          fetchPlatformRecentBusinesses.rejected,
          (
            state,
            action
          ) => {

            state.recentBusinessesLoading =
              false;

            state.recentBusinessesError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Unable to fetch recent businesses.",
              };

          }
        );


      /*
      |--------------------------------------------------------------------------
      | BUSINESS GROWTH
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformBusinessGrowth.pending,
          (
            state
          ) => {

            state.businessGrowthLoading =
              true;

            state.businessGrowthError =
              null;

          }
        )

        .addCase(
          fetchPlatformBusinessGrowth.fulfilled,
          (
            state,
            action
          ) => {

            state.businessGrowthLoading =
              false;

            state.businessGrowthError =
              null;

            state.businessGrowth =
              Array.isArray(
                action.payload
              )
                ? action.payload
                : action.payload
                    ?.records ||
                  action.payload
                    ?.growth ||
                  action.payload
                    ?.data ||
                  [];

          }
        )

        .addCase(
          fetchPlatformBusinessGrowth.rejected,
          (
            state,
            action
          ) => {

            state.businessGrowthLoading =
              false;

            state.businessGrowthError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Unable to fetch business growth.",
              };

          }
        );


      /*
      |--------------------------------------------------------------------------
      | SUBSCRIPTIONS
      |--------------------------------------------------------------------------
      */

      builder

        .addCase(
          fetchPlatformSubscriptionSummary.pending,
          (
            state
          ) => {

            state.subscriptionsLoading =
              true;

            state.subscriptionsError =
              null;

          }
        )

        .addCase(
          fetchPlatformSubscriptionSummary.fulfilled,
          (
            state,
            action
          ) => {

            state.subscriptionsLoading =
              false;

            state.subscriptionsError =
              null;

            state.subscriptions =
              action.payload ||
              null;

          }
        )

        .addCase(
          fetchPlatformSubscriptionSummary.rejected,
          (
            state,
            action
          ) => {

            state.subscriptionsLoading =
              false;

            state.subscriptionsError =
              action.payload ||
              {
                message:
                  action.error
                    ?.message ||
                  "Unable to fetch subscription summary.",
              };

          }
        );

    },

  });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  resetPlatformDashboard,
  clearPlatformDashboardErrors,
} =
  platformDashboardSlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectPlatformDashboard = (
  state
) =>
  state.platformDashboard
    ?.dashboard ||
  null;


export const selectPlatformDashboardLoading = (
  state
) =>
  Boolean(
    state.platformDashboard
      ?.dashboardLoading
  );


export const selectPlatformDashboardError = (
  state
) =>
  state.platformDashboard
    ?.dashboardError ||
  null;


export const selectPlatformDashboardSummary = (
  state
) =>
  state.platformDashboard
    ?.summary ||
  null;


export const selectPlatformRecentBusinesses = (
  state
) =>
  state.platformDashboard
    ?.recentBusinesses ||
  [];


export const selectPlatformBusinessGrowth = (
  state
) =>
  state.platformDashboard
    ?.businessGrowth ||
  [];


export const selectPlatformSubscriptionSummary = (
  state
) =>
  state.platformDashboard
    ?.subscriptions ||
  null;


export const selectPlatformDashboardAnyLoading = (
  state
) => {

  const platformDashboard =
    state.platformDashboard ||
    {};

  return Boolean(
    platformDashboard
      .dashboardLoading ||
    platformDashboard
      .summaryLoading ||
    platformDashboard
      .recentBusinessesLoading ||
    platformDashboard
      .businessGrowthLoading ||
    platformDashboard
      .subscriptionsLoading
  );

};


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default platformDashboardSlice.reducer;